/**
 * Service for managing projects.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface Project {
  id: string;
  user_id: string;
  name: string;
  display_name?: string;
  description?: string;
  sanitized_name: string;
  s3_base_path: string;
  config?: any;
  status?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
}

export interface ProjectWithItems {
  project: Project;
  data_files: any[];
  datasets: any[];
  training: any[];
  models: any[];
  inference: any[];
}

class ProjectService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all projects for the current user.
   */
  async listProjects(): Promise<Project[]> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/projects`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list projects');
    }

    const data = await response.json();
    return data.projects || [];
  }

  /**
   * Get a specific project by ID with all associated items.
   */
  async getProject(projectId: string): Promise<ProjectWithItems> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/projects/${projectId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get project');
    }

    return response.json();
  }
}

export const projectService = new ProjectService();
export default projectService;

